'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { AuditorListCard } from '@/components/landing/AuditorListCard';
import { AuditorListItem, api } from '@/lib/api';
import { fadeInUp } from '@/lib/animations';
import { SearchSortBar, SortBy, SortDirection } from '@/components/ui';

export default function AuditorsPage() {
  const t = useTranslations();
  const locale = useLocale();
  const [auditors, setAuditors] = useState<AuditorListItem[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('alphabetical');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [auditorList, countryList] = await Promise.all([
          api.getAuditors().catch(() => []),
          api.getCountries().catch(() => [])
        ]);

        setAuditors(auditorList || []);
        setCountries(countryList || []);
      } catch (error) {
        console.error('Error fetching auditors:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCountryName = (code: string) => {
    const country = countries.find(c => c.code === code);
    return country ? (locale === 'pt' ? country.name_pt : country.name_en) : code;
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSortChange = useCallback((by: SortBy, direction: SortDirection) => {
    setSortBy(by);
    setSortDirection(direction);
  }, []);

  const filteredAuditors = useMemo(() => {
    let result = [...auditors];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item =>
        (item.first_name?.toLowerCase().includes(query)) ||
        (item.last_name?.toLowerCase().includes(query)) ||
        (item.city?.toLowerCase().includes(query)) ||
        (getCountryName(item.country)?.toLowerCase().includes(query))
      );
    }

    // Apply sorting (alphabetical only, as auditors don't have date fields)
    result.sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return sortDirection === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    return result;
  }, [auditors, searchQuery, sortBy, sortDirection, countries]);

  return (
    <main className="min-h-screen bg-[#F9F9F9]">
      <section className="pt-24 pb-20 px-6 md:px-10">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="text-3xl font-medium tracking-tight text-black mb-1">
                {t('landing.auditorsSection.title')}
              </h1>
              <p className="text-[#888888]">
                {filteredAuditors.length === 1
                  ? t('landing.auditorsSection.count', { count: 1 })
                  : t('landing.auditorsSection.countPlural', { count: filteredAuditors.length })}
              </p>
            </div>

            <SearchSortBar
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              showUpdatedSort={false}
            />
          </div>

          {!isLoading && filteredAuditors.length > 0 ? (
            <motion.div
              key={`${searchQuery}-${sortBy}-${sortDirection}`}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="initial"
              animate="animate"
              variants={{
                initial: { opacity: 0 },
                animate: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
            >
              {filteredAuditors.map((auditor) => (
                <motion.div
                  key={auditor.id}
                  className="h-[280px]"
                  variants={{
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 }
                  }}
                >
                  <AuditorListCard
                    id={auditor.id}
                    firstName={auditor.first_name}
                    lastName={auditor.last_name}
                    city={auditor.city}
                    country={getCountryName(auditor.country)}
                    profilePictureUrl={api.getProfilePictureUrl(auditor.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-16 text-slate-400 italic bg-white border border-slate-200 rounded-lg">
              {isLoading ? 'Loading auditors...' : 'No auditors found.'}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
